import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  DeleteResellerMemberResponse,
  InviteResellerMemberResponse,
  MatrixRoleKey,
  ResellerRoleKey,
  ResellerRoleMatrix,
  RevokeResellerMemberResponse,
} from "@xop/dashboard-shared";

import { api } from "@/lib/api";

/** react-query key for one reseller's roster. Exported so the page and the
 * mutations can never drift into invalidating a key nobody reads. */
export function resellerMembersKey(resellerId: number | null): (string | number)[] {
  return ["admin", "reseller-members", resellerId ?? 0];
}

/** react-query key for one reseller's role matrix (spec F §7.2). Separate from
 * the roster key: a matrix edit changes what a ROLE grants, not any one
 * person's row, though both eventually reflect it once each is refetched. */
export function resellerMatrixKey(resellerId: number | null): (string | number)[] {
  return ["admin", "reseller-role-matrix", resellerId ?? 0];
}

/**
 * The four `/api/admin/resellers/:id/members` mutations, bundled so the panel
 * body stays about rendering.
 *
 * Every one of these is (or triggers) a TWO-SIDED write across crm-api and
 * portal-api, and the server reports a partial failure in the response body
 * rather than as an error status — `invited: false`, `account_disabled: false`.
 * So callers here must read the BODY, not just the absence of a throw. The
 * panel does exactly that and turns it into a warning toast; a caller that only
 * checked for a rejected promise would report a half-write as a success.
 */
export function useResellerMemberMutations(resellerId: number | null) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: resellerMembersKey(resellerId) });

  const invite = useMutation({
    mutationFn: async (input: { email: string; role: ResellerRoleKey; name?: string }) =>
      api<InviteResellerMemberResponse>(`/api/admin/resellers/${resellerId}/members`, {
        method: "POST",
        body: input,
      }),
    onSuccess: invalidate,
  });

  const setRole = useMutation({
    mutationFn: async ({ id, role }: { id: number; role: ResellerRoleKey }) =>
      api(`/api/admin/resellers/${resellerId}/members/${id}`, {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: invalidate,
  });

  const revoke = useMutation({
    mutationFn: async (id: number) =>
      api<RevokeResellerMemberResponse>(
        `/api/admin/resellers/${resellerId}/members/${id}/revoke`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });

  /** "Delete user" (2026-11-01): removes the membership AND the sign-in.
   * Read `account_deleted` — false means the sign-in survived. */
  const remove = useMutation({
    mutationFn: async (id: number) =>
      api<DeleteResellerMemberResponse>(`/api/admin/resellers/${resellerId}/members/${id}`, {
        method: "DELETE",
      }),
    onSuccess: invalidate,
  });

  const resendInvite = useMutation({
    mutationFn: async (id: number) =>
      api<InviteResellerMemberResponse>(
        `/api/admin/resellers/${resellerId}/members/${id}/resend`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });

  const invalidateMatrix = () => qc.invalidateQueries({ queryKey: resellerMatrixKey(resellerId) });
  const invalidateBoth = () => {
    void invalidateMatrix();
    void invalidate();
  };

  /** Per-person grant/deny overrides on top of the role (spec F §7.2). Only
   * the roster needs invalidating — the matrix (what the ROLE grants) does
   * not change when one person's overrides do. */
  const setPermissions = useMutation({
    mutationFn: async (input: { id: number; permission_grants: string[]; permission_denies: string[] }) =>
      api(`/api/admin/resellers/${resellerId}/members/${input.id}`, {
        method: "PATCH",
        body: { permission_grants: input.permission_grants, permission_denies: input.permission_denies },
      }),
    onSuccess: invalidate,
  });

  /** Overwrite one matrix row's permission bundle — a built-in or a custom
   * role — and, for a CUSTOM role only, rename it (`name`). crm-api applies
   * the closure rule on receipt, so the refreshed matrix (after invalidation)
   * may hold more than what was sent — every member of that role is affected,
   * hence both keys invalidate (a rename changes every holder's label too). */
  const setMatrix = useMutation({
    mutationFn: async (input: { role: MatrixRoleKey; permissions: string[]; name?: string }) =>
      api<ResellerRoleMatrix>(`/api/admin/resellers/${resellerId}/role-permissions/${input.role}`, {
        method: "PUT",
        body: { permissions: input.permissions, ...(input.name !== undefined ? { name: input.name } : {}) },
      }),
    onSuccess: invalidateBoth,
  });

  /** DELETE one matrix row. For a BUILT-IN that resets the company's row so
   * the shipped default applies again (including future changes to it); for
   * a CUSTOM role it removes the role — which crm-api refuses with 409
   * `ROLE_IN_USE` while anyone still holds it, so callers must handle that. */
  const resetMatrix = useMutation({
    mutationFn: async (role: MatrixRoleKey) =>
      api<ResellerRoleMatrix>(`/api/admin/resellers/${resellerId}/role-permissions/${role}`, {
        method: "DELETE",
      }),
    onSuccess: invalidateBoth,
  });

  /** Create a custom role at this company (2026-09-15). It starts with no
   * permissions; the operator ticks them in the matrix afterwards. */
  const createRole = useMutation({
    mutationFn: async (name: string) =>
      api<ResellerRoleMatrix>(`/api/admin/resellers/${resellerId}/role-permissions`, {
        method: "POST",
        body: { name, permissions: [] },
      }),
    onSuccess: invalidateBoth,
  });

  return { invite, setRole, revoke, remove, resendInvite, setPermissions, setMatrix, resetMatrix, createRole };
}
